import { getWayPayCodeIBM, getWayPayText } from '@shared-logic/payments';
import { Exchange, OrderExchange } from './order-exchange.interface';
import { Order } from './order.interface';
import { getBuyerExchange } from './utils';

/**
 * Where the IBM payment data of an exchange comes from:
 * - wayPay: the structured payment method of the exchange, converted by getWayPayCodeIBM.
 * - legacy: an exchange created before the structured payment method, sent with its codeWayPay.
 * - none: no valid payment method; the exchange contract must not be generated.
 */
export type ExchangePaymentIBMSource = 'wayPay' | 'legacy' | 'none';

export interface ExchangePaymentIBM {
    source: ExchangePaymentIBMSource;
    code: number; // PAGOBO
    days: number; // CANDBO, only meaningful when source is 'wayPay'
    expiration: string; // FEPABO source date, only meaningful when source is 'wayPay'
}

/**
 * Resolves the IBM payment code, days and expiration of an exchange.
 * The code is always our conversion of the exchange payment method, never the CERCPA code of the proforma.
 * A structured payment method without conversion does not fall back to codeWayPay.
 * @param order Exchange order. The buyer used by the conversion is the exchange buyer (COMPBO).
 * @returns ExchangePaymentIBM
 */
export function resolveExchangePaymentIBM(order: OrderExchange): ExchangePaymentIBM {
    const exchange = order?.exchange;
    const wayPay = exchange?.wayPay;

    if (wayPay && wayPay.wayPayName > 0) {
        const code = getWayPayCodeIBM(wayPay, exchange.typeCoin, getBuyerExchange(order));
        return code > 0
            ? { source: 'wayPay', code, days: wayPay.days, expiration: wayPay.expiration }
            : { source: 'none', code: 0, days: 0, expiration: '--' };
    }

    const legacyCode = Number(exchange?.codeWayPay);
    if (legacyCode > 0) {
        return { source: 'legacy', code: legacyCode, days: 0, expiration: '--' };
    }

    return { source: 'none', code: 0, days: 0, expiration: '--' };
}

/** Texts shown for the exchange payment method in the exchange detail. */
export interface ExchangePaymentDetail {
    wayPay: string; // Payment method, days and percentage
    ibmCode: string; // Code sent in PAGOBO
    expiration: string; // '--' when the payment method has no expiration
}

export const EXCHANGE_WITHOUT_WAY_PAY = 'Sin forma de pago';
export const EXCHANGE_LEGACY_WAY_PAY = 'Forma de pago anterior (código IBM)';

/**
 * Builds the texts of the exchange payment method for its detail, from the same resolution used to
 * send it to IBM, so the detail shows what the exchange contract will carry.
 * @param order Exchange order.
 * @returns ExchangePaymentDetail
 */
export function getExchangePaymentDetail(order: OrderExchange): ExchangePaymentDetail {
    const payment = resolveExchangePaymentIBM(order);

    if (payment.source === 'wayPay') {
        return { wayPay: getWayPayText(order.exchange.wayPay), ibmCode: String(payment.code), expiration: payment.expiration || '--' };
    }
    if (payment.source === 'legacy') {
        return { wayPay: EXCHANGE_LEGACY_WAY_PAY, ibmCode: String(payment.code), expiration: '--' };
    }
    return { wayPay: EXCHANGE_WITHOUT_WAY_PAY, ibmCode: '--', expiration: '--' };
}

/**
 * True when the exchange cannot be confirmed because backend-svc would reject its contract for the payment
 * method: the payment method does not convert to an IBM code, or it is empty and there is no legacy codeWayPay.
 * @param order Order that gives the exchange buyer (the mother, or the exchange buyer order).
 * @param exchange Exchange to check.
 * @returns boolean
 */
export function isExchangeWayPayBlockingConfirm(order: Order, exchange: Exchange): boolean {
    return resolveExchangePaymentIBM({ ...order, exchange }).source === 'none';
}
